import { useState, useEffect, useRef, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import SuiTypography from "components/SuiTypography";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";

import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";

import AddIcon from "@mui/icons-material/Add";
import YouTubeIcon from "@mui/icons-material/YouTube";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { getBase64, dataURLtoFile, matchYoutubeUrl } from "utils/imageHelper";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import MediaCard from "components/UpStyled/MediaCard";

import { nanoid } from "nanoid";
import { Upload } from "antd";
import { StoreContext } from "context/store";
import { AccountContext } from "context/account";

import api from "services/api";
import FeaturedGalleryList from "pages/de/new-portfolio/components/FeaturedGalleryList";
import UploadGalleryModal from "pages/de/portfolio/components/UploadGalleryModal";

const NewMediaInfo = ({ onNext, onPrev, isNextDisabled, isPrevDisabled }) => {
  const { store, updateStore } = useContext(StoreContext);
  const navigate = useNavigate();

  const [uploading, setUploading] = useState(false);
  const [addLinkVisible, setAddLinkVisible] = useState(false);
  const [link, setLink] = useState();
  const [uploadFileList, setUploadFileList] = useState([]);

  const [uploadGalleryVisible, setUploadGalleryVisible] = useState(false);

  const account = useContext(AccountContext);

  useEffect(() => {
    if (store && store.gallery && store.gallery.length > 0) {
      //transfer old media to new format
      if (!account?.metadata?.transfer_gallery_data) {
        handleTransferGallery(store.gallery);
      }
    }
  }, [store]);

  const handleTransferGallery = async (gallery = []) => {
    const slot = "gallery_0";
    let appendGallery = [];
    for (let i = 0; i < gallery.length; i++) {
      let url = gallery[i];
      let urlArr = url.split("/");

      let item = {
        name: urlArr[urlArr.length - 1],
        type: matchYoutubeUrl(url) ? "video" : "image/*",
        url: url,
      };
      appendGallery.push(item);
    }

    if (account.id) {
      let old_portfolio = account?.metadata?.portfolio_content || {};
      if (!old_portfolio[slot]) {
        old_portfolio[slot] = [];
      }

      //add new item
      for (let i = 0; i < appendGallery.length; i++) {
        old_portfolio[slot].push({
          img: appendGallery[i],
          type: "gallery",
        });
      }

      let payload = {
        metadata: {
          ...account?.metadata,
          portfolio_content: old_portfolio,
          transfer_gallery_data: true,
        },
      };

      await api.account.update(account.id, payload);
      setUploading(false);
      account.handleRefresh();
      toast.success("Update gallery successfully!");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    toast.success("Update gallery successfully");
    navigate("/wall/portfolio");
  };

  return (
    <Card id="media-info" sx={{ overflow: "visible" }} className="my-2">
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => 99999 }} open={uploading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className="flex justify-between items-center">
        <SuiBox px={3} pt={3}>
          <SuiTypography variant="h5">Images & Videos</SuiTypography>
        </SuiBox>
        <SuiBox px={3} pt={3}>
          <SuiButton
            color="dark"
            variant="gradient"
            size="small"
            sx={{ px: 1 }}
            onClick={() => setUploadGalleryVisible(true)}
          >
            Add Media
          </SuiButton>
        </SuiBox>
      </div>
      <SuiBox px={3} pb={3}>
        <SuiTypography variant="text" color="text">
          These may include factory photos, previous samples, team members, etc.
        </SuiTypography>
      </SuiBox>
      <SuiBox>
        <div>
          <FeaturedGalleryList />
        </div>
        {uploadGalleryVisible ? (
          <UploadGalleryModal
            handleClose={() => setUploadGalleryVisible(false)}
            visible={uploadGalleryVisible}
            handleRefresh={() => {
              account.handleRefresh();
            }}
          />
        ) : null}

        <SuiBox
          pt={3}
          px={3}
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          {onPrev ? (
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton
                  variant="gradient"
                  color={isPrevDisabled ? "light" : "dark"}
                  sx={{ height: "100%" }}
                  onClick={onPrev}
                  disabled={isPrevDisabled}
                >
                  <ArrowBackIcon fontSize="large" />
                </SuiButton>
              </SuiBox>
            </SuiBox>
          ) : (
            <div />
          )}
          <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
            <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
              <SuiButton
                variant="gradient"
                color={"dark"}
                sx={{ mb: 2 }}
                onClick={() => navigate("/my-portfolio")}
              >
                My Portfolio
              </SuiButton>
            </SuiBox>
          </SuiBox>
          {onNext ? (
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton
                  variant="gradient"
                  color={isNextDisabled ? "light" : "dark"}
                  sx={{ height: "100%" }}
                  onClick={onNext}
                  disabled={isNextDisabled}
                >
                  <ArrowForwardIcon fontSize="large" />
                </SuiButton>
              </SuiBox>
            </SuiBox>
          ) : null}
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default NewMediaInfo;
